import { useCallback, useEffect, useRef, useState } from 'react';
import { alertsService } from '../../../services/alerts/alertsService';
import { incidentApi } from '../../../services/security/incidentApi';
import recommendationAPI from '../../../services/api/recommendationClient';
import { INBOX_UNREAD_EVENT, refreshInboxUnread } from '../../../hooks/useInboxUnread';
import { isFeatureEnabled } from '../../../utils/featureFlags';

/** Approvals keep the once-a-minute refresh the bell has always had. */
const APPROVALS_POLL_MS = 60000;
/** Unread alerts loaded into the panel. Mark-all-read acknowledges at most these. */
export const ALERT_PAGE_SIZE = 20;
/** The engine's ceiling; assignment is filtered here because the API has no assignee filter. */
const REC_SCAN_LIMIT = 500;

const statusOf = (e) => Number(e?.status ?? e?.response?.status) || null;
/**
 * A source the user has no access to is not an outage: 401/403 hides it quietly,
 * the way the bell always treated a user without Security. Anything else is
 * reported, because "Couldn't load" and "nothing to show" are different claims.
 */
const isNoAccess = (e) => [401, 403].includes(statusOf(e));

/**
 * One source's state. `items` is null until the first answer lands, so the
 * panel can tell "not loaded yet" from "loaded, empty".
 */
const initial = { items: null, error: null, more: false };

/**
 * Data for the header Notifications panel. Each source loads and fails on its
 * own: an alerts outage must not hide a pending approval.
 *
 * @param {object} opts
 * @param {string} opts.email         the signed-in user's email (assignee match)
 * @param {boolean} opts.securityOpen the Security module is openable — approvals
 *                                    are Security work, so no fetch without it
 */
export default function useHeaderNotifications({ email = '', securityOpen = true } = {}) {
  const [approvals, setApprovals] = useState(initial);
  const [alerts, setAlerts] = useState(initial);
  const [recs, setRecs] = useState(initial);
  const [marking, setMarking] = useState(false);
  const mounted = useRef(true);
  // Set on every mount: StrictMode (dev) mounts twice, and a flag left false by the
  // first cleanup would drop every result.
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const recsEnabled = isFeatureEnabled('RECOMMENDATIONS');
  const me = String(email || '').trim().toLowerCase();

  const fetchApprovals = useCallback(async () => {
    if (!securityOpen) { setApprovals({ items: [], error: null, more: false }); return; }
    try {
      const data = await incidentApi.listApprovalsPlatformWide({ status: 'pending', mine: true });
      const list = Array.isArray(data?.approvals) ? data.approvals : [];
      if (mounted.current) setApprovals({ items: list.slice(0, 10), error: null, more: list.length > 10 });
    } catch (e) {
      if (!mounted.current) return;
      setApprovals(isNoAccess(e) ? { items: [], error: null, more: false } : { items: [], error: e, more: false });
    }
  }, [securityOpen]);

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await alertsService.getAlerts({ page: 1, page_size: ALERT_PAGE_SIZE, unread_only: true });
      const list = Array.isArray(data?.alerts) ? data.alerts : (Array.isArray(data) ? data : []);
      const unread = list.filter((a) => !a.is_read);
      const total = Number(data?.total);
      if (mounted.current) {
        setAlerts({ items: unread, error: null, more: Number.isFinite(total) && total > unread.length });
      }
    } catch (e) {
      if (!mounted.current) return;
      setAlerts(isNoAccess(e) ? { items: [], error: null, more: false } : { items: [], error: e, more: false });
    }
  }, []);

  const fetchRecs = useCallback(async () => {
    if (!recsEnabled || !me) { setRecs({ items: [], error: null, more: false }); return; }
    try {
      // Open statuses = the engine default (active + escalated), the Inbox's
      // "Open" view. `audience` stays 'all': 'mine' means "addressed to my
      // role" (and includes every untargeted finding), which is not the same
      // thing as assigned to me — assignment is the `assignee` field.
      const res = await recommendationAPI.list({ audience: 'all', limit: REC_SCAN_LIMIT });
      const items = Array.isArray(res?.items) ? res.items : [];
      const mine = items.filter((r) => String(r.assignee || '').trim().toLowerCase() === me);
      if (mounted.current) setRecs({ items: mine, error: null, more: false });
    } catch (e) {
      if (!mounted.current) return;
      setRecs(isNoAccess(e) ? { items: [], error: null, more: false } : { items: [], error: e, more: false });
    }
  }, [recsEnabled, me]);

  // Approvals: on mount and once a minute.
  useEffect(() => {
    fetchApprovals();
    const t = setInterval(fetchApprovals, APPROVALS_POLL_MS);
    return () => clearInterval(t);
  }, [fetchApprovals]);

  // Alerts: on mount, and whenever an alerts page reports it marked some read.
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);
  useEffect(() => {
    const onRead = () => fetchAlerts();
    window.addEventListener('alerts-marked-read', onRead);
    return () => window.removeEventListener('alerts-marked-read', onRead);
  }, [fetchAlerts]);

  // Recommendations: on mount and whenever the Inbox reports a read-state change.
  useEffect(() => { fetchRecs(); }, [fetchRecs]);
  useEffect(() => {
    if (!recsEnabled) return undefined;
    // The Inbox sends a delta on the click and a bare event once the write is
    // durable; re-read on the bare one only, so a click costs one request.
    const onChanged = (e) => { if (typeof e?.detail?.delta !== 'number') fetchRecs(); };
    window.addEventListener(INBOX_UNREAD_EVENT, onChanged);
    return () => window.removeEventListener(INBOX_UNREAD_EVENT, onChanged);
  }, [recsEnabled, fetchRecs]);

  /** Re-read everything — the panel calls this when it opens. */
  const refresh = useCallback(() => {
    fetchApprovals();
    fetchAlerts();
    fetchRecs();
  }, [fetchApprovals, fetchAlerts, fetchRecs]);

  /**
   * Mark what the panel shows as read.
   *
   * Alerts are acknowledged one by one, bounded to the unread alerts loaded
   * here (≤ ALERT_PAGE_SIZE) and only those the caller passes — the panel
   * passes the ones the user can open. The bulk `acknowledge-all` endpoint is
   * deliberately not used: it acknowledges every alert in the organisation,
   * across modules this person cannot see.
   * Assigned recommendations are marked read for this reader only.
   * Approvals cannot be "read" — they stay until someone decides them.
   */
  const markAllRead = useCallback(async ({ alertIds = [], recIds = [] } = {}) => {
    setMarking(true);
    try {
      await Promise.allSettled([
        ...alertIds.slice(0, ALERT_PAGE_SIZE).map((id) => alertsService.markAlertRead(id)),
        ...recIds.map((id) => recommendationAPI.markRead(id, true)),
      ]);
      if (alertIds.length) window.dispatchEvent(new CustomEvent('alerts-marked-read'));
      if (recIds.length) refreshInboxUnread();
    } finally {
      // The two events above make the listeners re-read both sources.
      if (mounted.current) setMarking(false);
    }
  }, []);

  /** Opening a finding from here reads it, as opening it in the Inbox does. */
  const markRecRead = useCallback((recId) => {
    recommendationAPI.markRead(recId, true).then(refreshInboxUnread).catch(() => {});
  }, []);

  return {
    approvals, alerts, recs, recsEnabled, marking,
    refresh, retry: { approvals: fetchApprovals, alerts: fetchAlerts, recs: fetchRecs },
    markAllRead, markRecRead,
  };
}
